import _toConsumableArray from "/codebuild/output/src908941260/src/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _slicedToArray from "/codebuild/output/src908941260/src/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _asyncToGenerator from "/codebuild/output/src908941260/src/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _regeneratorRuntime from "/codebuild/output/src908941260/src/node_modules/@babel/runtime/regenerator/index.js";
import { devAssert } from '../jsutils/devAssert.mjs';
import { inspect } from '../jsutils/inspect.mjs';
import { isAsyncIterable } from '../jsutils/isAsyncIterable.mjs';
import { addPath, pathToArray } from '../jsutils/Path.mjs';
import { GraphQLError } from '../error/GraphQLError.mjs';
import { locatedError } from '../error/locatedError.mjs';
import { collectFields } from './collectFields.mjs';
import { assertValidExecutionArguments, buildExecutionContext, buildResolveInfo, execute, getFieldDef } from './execute.mjs';
import { mapAsyncIterator } from './mapAsyncIterator.mjs';
import { getArgumentValues } from './values.mjs';
/**
 * Implements the "Subscribe" algorithm described in the GraphQL specification.
 *
 * Returns a Promise which resolves to either an AsyncIterator (if successful)
 * or an ExecutionResult (error). The promise will be rejected if the schema or
 * other arguments to this function are invalid, or if the resolved event stream
 * is not an async iterable.
 *
 * If the client-provided arguments to this function do not result in a
 * compliant subscription, a GraphQL Response (ExecutionResult) with
 * descriptive errors and no data will be returned.
 *
 * If the source stream could not be created due to faulty subscription
 * resolver logic or underlying systems, the promise will resolve to a single
 * ExecutionResult containing `errors` and no `data`.
 *
 * If the operation succeeded, the promise resolves to an AsyncIterator, which
 * yields a stream of ExecutionResults representing the response stream.
 *
 * Accepts either an object with named arguments, or individual arguments.
 */

export function subscribe(_x) {
  return _subscribe.apply(this, arguments);
}
/**
 * Implements the "CreateSourceEventStream" algorithm described in the
 * GraphQL specification, resolving the subscription source event stream.
 *
 * Returns a Promise which resolves to either an AsyncIterable (if successful)
 * or an ExecutionResult (error). The promise will be rejected if the schema or
 * other arguments to this function are invalid, or if the resolved event stream
 * is not an async iterable.
 *
 * If the client-provided arguments to this function do not result in a
 * compliant subscription, a GraphQL Response (ExecutionResult) with
 * descriptive errors and no data will be returned.
 *
 * If the the source stream could not be created due to faulty subscription
 * resolver logic or underlying systems, the promise will resolve to a single
 * ExecutionResult containing `errors` and no `data`.
 *
 * If the operation succeeded, the promise resolves to the AsyncIterable for the
 * event stream returned by the resolver.
 *
 * A Source Event Stream represents a sequence of events, each of which triggers
 * a GraphQL execution for that event.
 *
 * This may be useful when hosting the stateful subscription service in a
 * different process or machine than the stateless GraphQL execution engine,
 * or otherwise separating these two steps. For more on this, see the
 * "Supporting Subscriptions at Scale" information in the GraphQL specification.
 */

function _subscribe() {
  _subscribe = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(args) {
    var schema,
        document,
        rootValue,
        contextValue,
        variableValues,
        operationName,
        fieldResolver,
        subscribeFieldResolver,
        resultOrStream,
        mapSourceToResponse,
        _args = arguments;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            // Temporary for v15 to v16 migration. Remove in v17
            _args.length < 2 || devAssert(false, 'graphql@16 dropped long-deprecated support for positional arguments, please pass an object instead.');
            schema = args.schema, document = args.document, rootValue = args.rootValue, contextValue = args.contextValue, variableValues = args.variableValues, operationName = args.operationName, fieldResolver = args.fieldResolver, subscribeFieldResolver = args.subscribeFieldResolver;
            _context.next = 4;
            return createSourceEventStream(schema, document, rootValue, contextValue, variableValues, operationName, subscribeFieldResolver);

          case 4:
            resultOrStream = _context.sent;

            if (isAsyncIterable(resultOrStream)) {
              _context.next = 7;
              break;
            }

            return _context.abrupt("return", resultOrStream);

          case 7:
            // For each payload yielded from a subscription, map it over the normal
            // GraphQL `execute` function, with `payload` as the rootValue.
            // This implements the "MapSourceToResponseEvent" algorithm described in
            // the GraphQL specification. The `execute` function provides the
            // "ExecuteSubscriptionEvent" algorithm, as it is nearly identical to the
            // "ExecuteQuery" algorithm, for which `execute` is also used.
            mapSourceToResponse = function mapSourceToResponse(payload) {
              return execute({
                schema: schema,
                document: document,
                rootValue: payload,
                contextValue: contextValue,
                variableValues: variableValues,
                operationName: operationName,
                fieldResolver: fieldResolver
              });
            }; // Map every source value to a ExecutionResult value as described above.


            return _context.abrupt("return", mapAsyncIterator(resultOrStream, mapSourceToResponse));

          case 9:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _subscribe.apply(this, arguments);
}

export function createSourceEventStream(_x2, _x3, _x4, _x5, _x6, _x7, _x8) {
  return _createSourceEventStream.apply(this, arguments);
}

function _createSourceEventStream() {
  _createSourceEventStream = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(schema, document, rootValue, contextValue, variableValues, operationName, subscribeFieldResolver) {
    var exeContext, eventStream;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            // If arguments are missing or incorrectly typed, this is an internal
            // developer mistake which should throw an early error.
            assertValidExecutionArguments(schema, document, variableValues); // If a valid execution context cannot be created due to incorrect arguments,
            // a "Response" with only errors is returned.

            exeContext = buildExecutionContext({
              schema: schema,
              document: document,
              rootValue: rootValue,
              contextValue: contextValue,
              variableValues: variableValues,
              operationName: operationName,
              subscribeFieldResolver: subscribeFieldResolver
            }); // Return early errors if execution context failed.

            if ('schema' in exeContext) {
              _context2.next = 4;
              break;
            }

            return _context2.abrupt("return", {
              errors: exeContext
            });

          case 4:
            _context2.prev = 4;
            _context2.next = 7;
            return executeSubscription(exeContext);

          case 7:
            eventStream = _context2.sent;

            if (isAsyncIterable(eventStream)) {
              _context2.next = 10;
              break;
            }

            throw new Error('Subscription field must return Async Iterable. ' + "Received: ".concat(inspect(eventStream), "."));

          case 10:
            return _context2.abrupt("return", eventStream);

          case 13:
            _context2.prev = 13;
            _context2.t0 = _context2["catch"](4);

            if (!(_context2.t0 instanceof GraphQLError)) {
              _context2.next = 17;
              break;
            }

            return _context2.abrupt("return", {
              errors: [_context2.t0]
            });

          case 17:
            throw _context2.t0;

          case 18:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2, null, [[4, 13]]);
  }));
  return _createSourceEventStream.apply(this, arguments);
}

function executeSubscription(_x9) {
  return _executeSubscription.apply(this, arguments);
}

function _executeSubscription() {
  _executeSubscription = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(exeContext) {
    var schema, fragments, operation, variableValues, rootValue, rootType, rootFields, _, responseName, fieldNodes, fieldDef, fieldName, path, info, _fieldDef$subscribe, args, _contextValue, resolveFn, eventStream;

    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            schema = exeContext.schema, fragments = exeContext.fragments, operation = exeContext.operation, variableValues = exeContext.variableValues, rootValue = exeContext.rootValue;
            rootType = schema.getSubscriptionType();

            if (!(rootType == null)) {
              _context3.next = 4;
              break;
            }

            throw new GraphQLError('Schema is not configured to execute subscription operation.', {
              nodes: operation
            });

          case 4:
            rootFields = collectFields(schema, fragments, variableValues, rootType, operation.selectionSet);
            _ = _slicedToArray(_toConsumableArray(rootFields.entries())[0], 2), responseName = _[0], fieldNodes = _[1];
            fieldDef = getFieldDef(schema, rootType, fieldNodes[0]);

            if (fieldDef) {
              _context3.next = 10;
              break;
            }

            fieldName = fieldNodes[0].name.value;
            throw new GraphQLError("The subscription field \"".concat(fieldName, "\" is not defined."), {
              nodes: fieldNodes
            });

          case 10:
            path = addPath(undefined, responseName, rootType.name);
            info = buildResolveInfo(exeContext, fieldDef, fieldNodes, rootType, path);
            _context3.prev = 12;
            // Implements the "ResolveFieldEventStream" algorithm from GraphQL specification.
            // It differs from "ResolveFieldValue" due to providing a different `resolveFn`.
            // Build a JS object of arguments from the field.arguments AST, using the
            // variables scope to fulfill any variable references.
            args = getArgumentValues(fieldDef, fieldNodes[0], variableValues); // The resolve function's optional third argument is a context value that
            // is provided to every resolve function within an execution. It is commonly
            // used to represent an authenticated user, or request-specific caches.

            _contextValue = exeContext.contextValue; // Call the `subscribe()` resolver or the default resolver to produce an
            // AsyncIterable yielding raw payloads.

            resolveFn = (_fieldDef$subscribe = fieldDef.subscribe) !== null && _fieldDef$subscribe !== void 0 ? _fieldDef$subscribe : exeContext.subscribeFieldResolver;
            _context3.next = 18;
            return resolveFn(rootValue, args, _contextValue, info);

          case 18:
            eventStream = _context3.sent;

            if (!(eventStream instanceof Error)) {
              _context3.next = 21;
              break;
            }

            throw eventStream;

          case 21:
            return _context3.abrupt("return", eventStream);

          case 24:
            _context3.prev = 24;
            _context3.t0 = _context3["catch"](12);
            throw locatedError(_context3.t0, fieldNodes, pathToArray(path));

          case 27:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3, null, [[12, 24]]);
  }));
  return _executeSubscription.apply(this, arguments);
}